import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MessageService, ConfirmationService } from 'primeng/api';
import { Component, OnInit, Input, EventEmitter, Output, Inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { AlertService } from '@app/_services';
import * as moment from 'moment';
import { PrimeNGConfig } from 'primeng/api';
import { OccDaysToArrivalService } from '../occ-days-matrix.service';
import { OccDaysToArrival } from '../occ-days-matrix.component';
import { OccDeviationService } from '../../occ-deviation';
import { OccProfileService } from '../../occ-profile';
import { AddDayDialogComponent } from '../add-day-dialog/add-day-dialog.component';
import { OccDeviationComponent } from '../../occ-deviation/occ-deviation/occ-deviation.component';
import { OccDayDetailsDialogComponent } from '../occ-day-details-dialog/occ-day-details-dialog.component';
import { OCC_METRIC_TOOLTIPS } from '../occ-metric-tooltips';

interface PivotRow {
  occDeviationId: number;
  lowEndExpOccPCT: number;
  highEndExpOccPCT: number;
  pctByDay: { [day: number]: DayMetrics };
}

interface DayMetrics {
  pct?: number;
}

@Component({
    selector: 'app-occ-days-matrix',
    templateUrl: './occ-days-matrix.component.html',
    styleUrls: ['./occ-days-matrix.component.css'],
    providers: [MessageService, ConfirmationService],
    standalone: false
})
export class OccDaysMatrixComponent implements OnInit {

	pivotRows: PivotRow[] = [];
	daysList: any[] = []; // dynamic column headers
	selectedProfileId: number = null;
	profileOptions: any[] = []; // for <p-dropdown>
	editBuffer: { [occDeviationId: number]: { [day: number]: number } } = {};
	metricTooltips = OCC_METRIC_TOOLTIPS;

	loading: boolean = false;

	constructor(
		private occDeviationService: OccDeviationService,
		private occDaysService: OccDaysToArrivalService,
		private occProfileService: OccProfileService,
		private dialog: MatDialog,
		private confirmationService: ConfirmationService,
		private messageService: MessageService,
		private alertService: AlertService,
	) {}

	ngOnInit(): void {
		this.loadProfileOptions(); // Load profiles on init
	}

	loadProfileOptions(): void {
		this.occProfileService.findAll().subscribe({
		next: (res: any) => {
				this.profileOptions = res.data.occProfiles.map(profile => ({
				label: profile.occProfileName, 
				value: profile.occProfileId
			}));
		},
		error: err => this.alertService.error(err)
		});
	}
	onProfileChange(profileId: number): void {
		if (!profileId) return;

		this.selectedProfileId = profileId;
		this.loadMatrix(profileId);
	}
	
	loadMatrix(profileId: number) {
		this.loading = true;
		Promise.all([
			this.occDeviationService.getByProfile(profileId).toPromise(),
			this.occDaysService.getByProfile(profileId).toPromise()
		]).then(([deviationsRes, daysRes]) => {
			const deviations: any = (deviationsRes as any).occDeviations;
			const days = (daysRes as any).occDaysToArrivals;

			// Collect all unique days
			const uniqueDays = Array.from(
				new Set<number>(days.map(d => Number(d.days)))
				).filter((d): d is number => !isNaN(d)).sort((a, b) => a - b);

			this.daysList = uniqueDays;
			// Create a map: { [occDeviationId]: { [days]: DayMetrics } }
			const metricsMap: { [occDeviationId: number]: { [day: number]: DayMetrics } } = {};
			for (let d of days) {
				if (!metricsMap[d.occDeviationId]) {
					metricsMap[d.occDeviationId] = {};
				}
				metricsMap[d.occDeviationId][d.days] = {
					pct: d.pct
				};
			}

			this.pivotRows = deviations.map(dev => ({
				occDeviationId: dev.occDeviationId,
				lowEndExpOccPCT: dev.lowEndExpOccPCT,
				highEndExpOccPCT: dev.highEndExpOccPCT,
				pctByDay: metricsMap[dev.occDeviationId] || {}
			}));
		}).catch(err => {
			console.error('Matrix load failed:', err);
		}).finally(() => {
			this.loading = false;
		});
	}


	openAddDayDialog() {

		if (!this.selectedProfileId) {
			this.messageService.add({
			severity: 'warn',
			summary: 'Select Profile',
			detail: 'Please select an occupancy profile first.',
			life: 3000
			});
			return;
		}
		const dialogRef = this.dialog.open(AddDayDialogComponent);
		dialogRef.componentInstance.existingDays = [...this.daysList]; // pass existing days

		dialogRef.afterClosed().subscribe((result: any) => {
			if (!result) return;

			const {
				day,
				defaultPct,
				defaultMinStayExpectedNTS,
				defaultShortTermGapMultiplierPCT,
				defaultShortTermGapMinNumNightsRevNTS,
				defaultLongTermGapMultiplierPCT,
				defaultLongTermGapEmptyMultiplierPCT,
				defaultConnectingFrontOrBackMultiplierPCT,
				defaultConnectingFrontAndBackMultiplierPCT
				} = result;

			if (this.daysList.includes(day)) {
			this.messageService.add({
				severity: 'warn',
				summary: 'Duplicate Day',
				detail: `Day ${day} already exists.`,
				life: 3000
			});
			return;
			}

			this.daysList.push(day);
			this.daysList.sort((a, b) => a - b);

			const defaultDecimal = defaultPct / 100;

			for (let row of this.pivotRows) {
				row.pctByDay[day] = {
				pct: defaultPct / 100
			};
			if (!this.editBuffer[row.occDeviationId]) {
				this.editBuffer[row.occDeviationId] = {};
			}
			this.editBuffer[row.occDeviationId][day] = defaultPct;

			
			this.occDaysService.upsert({
				occDeviationId: row.occDeviationId,
				days: day,
				pct: defaultPct / 100
			}).subscribe({
				next: () => {},
				error: err => {
				console.error(`Error adding day ${day} for deviation ${row.occDeviationId}`, err);
				}
			});
			}

			this.messageService.add({
			severity: 'success',
			summary: 'Day Added',
			detail: `Day ${day} added with default ${defaultPct}%`,
			life: 3000
			});
		});
	}

	confirmDeleteDay(day: number) {
		this.confirmationService.confirm({
			message: `Are you sure you want to delete day ${day}?`,
			header: 'Delete Confirmation',
			icon: 'pi pi-exclamation-triangle',
			accept: () => {
			this.deleteDay(day);
			}
		});
	}

	deleteDay(day: number): void {
		this.occDaysService.deleteDayForProfile(this.selectedProfileId, day).subscribe({
			next: () => {
			// Update local state after successful delete
			for (let row of this.pivotRows) {
				delete row.pctByDay[day];
				delete this.editBuffer[row.occDeviationId]?.[day];
			}

			this.daysList = this.daysList.filter(d => d !== day);

			this.alertService.success(`Day ${day} removed from matrix and database.`);
			},
			error: err => {
			console.error('Delete failed', err);
			this.alertService.error('Failed to delete day from database.');
			}
		});
	}

	onOpenFormAdd() {

		if (!this.selectedProfileId) {
			this.messageService.add({
			severity: 'warn',
			summary: 'Select Profile',
			detail: 'Please select an occupancy profile first.',
			life: 3000
			});
			return;
		}

		const dialogConfig = new MatDialogConfig();

		// settings
		dialogConfig.disableClose = false;
		dialogConfig.autoFocus = true;
		dialogConfig.hasBackdrop = true;
		// Set width and height
		// dialogConfig.width = '800px'; 
		// dialogConfig.height = '600px';

		// pass data, ie rateBuicket: this.shortTermRentalId,
		dialogConfig.data = {
			title: 'Add OccDeviation',
			selectedProfileId: this.selectedProfileId
		};

		// open the popup and get a reference to it called dialogRef
		const dialogRef = this.dialog.open(OccDeviationComponent, dialogConfig);

		// to receive the added element, subscribe to the eventEmitter we created 'doAdd' in the element to handle adding
		dialogRef.componentInstance.doAdd.subscribe(
			data => {
				console.log('Dialog output:', data);
				dialogRef.close();
				// reload deviations + days from backend
				if (this.selectedProfileId) {
				this.loadMatrix(this.selectedProfileId);
				}
			});
	}


	formatPct(value?: number): string {
		if (value !== undefined && value !== null && !isNaN(value))
		{
			return `${(value * 100).toFixed(0)}%`;
		}
		else
		{
			return '-';
		}
	}

	openDayDetails(row: PivotRow, day: number) {

		const deviationOptions = this.pivotRows.map(r => ({
			value: r.occDeviationId,
			label: `Low: ${(r.lowEndExpOccPCT * 100).toFixed(0)}%  | High: ${(r.highEndExpOccPCT * 100).toFixed(0)}%`
		}));

		const dayOptions = this.daysList.map(d => ({
			label: `${d} days`,
			value: d
		}));

		const dialogRef = this.dialog.open(OccDayDetailsDialogComponent, {
			width: '900px',
			height: '650px',
			maxWidth: '95vw',
			maxHeight: '95vh',
			panelClass: 'occ-day-details-dialog',
			data: {
				deviationOptions,
				dayOptions,
				selectedDeviationIds: [row.occDeviationId],
				selectedDays: [day],
				metrics: row.pctByDay[day]
			}
		});

		dialogRef.afterClosed().subscribe(result => {
			if (!result) return;

			this.occDaysService.bulkUpdate(result).subscribe({
				next: () => {
				this.messageService.add({
					severity: 'success',
					summary: 'Updated',
					detail: 'Values updated successfully'
				});

				// reload matrix
				this.loadMatrix(this.selectedProfileId);
				},
				error: err => this.alertService.error(err)
			});
		});
	}
}
